import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import { LinkButton } from '@enamelai/design-system'
import { Icon as ArrowLeftIcon } from '@enamelai/design-system/icons/Arrow_2_left'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportDataTable, {
  ReportDataTableColumn,
  type ReportDataTableFullViewSearchConfig,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import { useReportInPlaceExpandOptional } from '@components/pages/Reports/shared/ReportInPlaceExpandContext'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import {
  AT_RISK_AGE_BUCKET_META,
  AT_RISK_CARD_ROW_LIMIT,
  AT_RISK_LEVEL_FILTER_TABS,
  AT_RISK_TABLE_FULL_VIEW_SEARCH_FILTERS,
  PATIENT_BASE_HEALTH_FILTERS,
  PATIENT_BASE_HEALTH_KPI_DEFINITIONS,
} from '@constants/Reports/patientBaseHealth.constants'
import {
  AtRiskAgeBucket,
  AtRiskLevelFilter,
  AtRiskSummaryMetric,
  RiskPatientRow,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { usePatientBaseHealthReport } from '@hooks/reports/usePatientBaseHealthReport'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import { formatCurrency } from '@utils/dashboardFinance.util'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { formatReportKpiFooter } from '@utils/reports/reportKpiFooter.util'
import { formatKpiPercent } from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

type AxisTickPayload = {
  value?: string
}

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'

/**
 * Recharts vertical bar charts: space between category bands (the “columns”).
 * This is a share of each band’s width, so spacing scales with chart width — not a fixed pixel distance.
 * Use the same value across report bar charts so rhythm stays consistent.
 */
const REPORT_BAR_CATEGORY_GAP = '16%'

/**
 * Recharts margins — small positive `left` adds a little inset from the card edge
 * so the chart doesn’t feel flush against the border.
 */
const REPORT_LINE_CHART_MARGIN = { top: 8, right: 12, left: 4, bottom: 2 }
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }

/** Y-axis width (px) — keep only enough for tick labels; wider values steal plot width. */
const REPORT_YAXIS_WIDTH = 34
const REPORT_YAXIS_WIDTH_WIDE = 46

const PBH_AT_RISK_FULL_VIEW_SEARCH: ReportDataTableFullViewSearchConfig<RiskPatientRow> =
  {
    filters: AT_RISK_TABLE_FULL_VIEW_SEARCH_FILTERS,
    filterLabels: { riskLevel: 'Risk level' },
    filterCategoryToRowKey: { riskLevel: 'riskLevel' },
    placeholder: 'Search patients',
  }

function formatKpiCount(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('en-US')
}

function formatSignedCount(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  const rounded = Math.round(value)
  if (rounded > 0) return `+${rounded.toLocaleString('en-US')}`
  return rounded.toLocaleString('en-US')
}

function formatDollarK(value: number): string {
  if (value === 0) return '$0'
  const k = value / 1000
  if (Math.abs(k) >= 1) {
    return Number.isInteger(k) ? `$${k}K` : `$${k.toFixed(1)}K`
  }
  return formatCurrency(value, { compact: true })
}

function computeCountAxis(values: number[]): {
  ticks: number[]
  width: number
  formatter: (value: number) => string
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax = max <= 0 ? 10 : Math.ceil((max * 1.15) / 5) * 5 || 10
  const step = axisMax <= 20 ? 5 : axisMax <= 100 ? 20 : Math.ceil(axisMax / 4)
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(t)
  return {
    ticks,
    width: REPORT_YAXIS_WIDTH_WIDE,
    formatter: (value: number) =>
      value >= 1000 ? `${(value / 1000).toFixed(1)}K` : `${value}`,
  }
}

function VisitRangeTick(props: {
  x?: number
  y?: number
  payload?: AxisTickPayload
}): React.ReactElement | null {
  const { x, y, payload } = props
  if (x == null || y == null || !payload?.value) return null
  const [rangePart, unitPart = ''] = payload.value.split(' ')

  return (
    <g transform={`translate(${x},${y + 10})`}>
      <text
        x={0}
        y={0}
        textAnchor="middle"
        fill="var(--gray-500)"
        fontSize={12}
      >
        <tspan x={0} dy="0">
          {rangePart}
        </tspan>
        <tspan x={0} dy="13">
          {unitPart}
        </tspan>
      </text>
    </g>
  )
}

function AtRiskSummaryStrip({
  metrics,
}: {
  metrics: AtRiskSummaryMetric[]
}): React.ReactElement {
  return (
    <div className="reports-patient-base-health__risk-summary-grid">
      {metrics.map((metric) => (
        <article
          key={metric.id}
          className={[
            'reports-patient-base-health__risk-summary-item',
            metric.labelTone === 'danger'
              ? 'reports-patient-base-health__risk-summary-item--danger'
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <h4>{metric.label}</h4>
          <p>{metric.value}</p>
        </article>
      ))}
    </div>
  )
}

function PatientBaseHealthReport(): React.ReactElement {
  const [atRiskLevelFilter, setAtRiskLevelFilter] =
    useState<AtRiskLevelFilter>('all')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const expandCtx = useReportInPlaceExpandOptional()
  const atRiskFullViewTableOnly = expandCtx?.expandedSectionId === 'pbh-at-risk'

  const { loading, error, data } = usePatientBaseHealthReport(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage: 'Failed to load patient base health data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return PATIENT_BASE_HEALTH_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer || '—',
        unitInline: def.unitInline,
        valueTone: def.valueTone,
        value: loading ? '…' : '—',
        tone: 'neutral',
      }))
    }

    const valueById: Record<string, number> = {
      'total-active-patients': data.totalActivePatients.value,
      'net-active-growth': data.netActiveGrowth.value,
      'retention-rate': data.retentionRate.value,
      'reactivation-rate': data.reactivationRate.value,
      'at-risk-patient': data.atRiskPatientCount.value,
    }

    const priorById: Record<string, number> = {
      'total-active-patients': data.totalActivePatientsFooter.value,
      // Net growth footer uses prior active base, not prior growth.
      'net-active-growth': data.totalActivePatientsFooter.value,
      'retention-rate': data.retentionRateFooter.value,
      'reactivation-rate': data.reactivationRateFooter.value,
      'at-risk-patient': data.atRiskPatientCountFooter.value,
    }

    return PATIENT_BASE_HEALTH_KPI_DEFINITIONS.map((def) => {
      const raw = valueById[def.id]
      let value: string
      if (def.format === 'percent') value = formatKpiPercent(raw)
      else if (def.format === 'signedCount') value = formatSignedCount(raw)
      else value = formatKpiCount(raw)

      const footerResult = formatReportKpiFooter(
        def.footerMode ?? 'percentChange',
        raw,
        priorById[def.id],
      )

      return {
        id: def.id,
        title: def.title,
        footer: footerResult.text,
        tone: footerResult.tone,
        unitInline: def.unitInline,
        valueTone: def.valueTone,
        value,
      }
    })
  }, [data, loading])

  const trendRows = useMemo(() => {
    if (!data) return []
    return data.patientPopulationTrend.buckets.map((row) => ({
      month: row.label,
      Active: row.active,
      Dormant: row.dormant,
      Lost: row.lost,
    }))
  }, [data])

  const visitChartData = useMemo(() => {
    if (!data) return []
    return data.lastVisitDistribution.buckets.map((row) => ({
      label: row.label,
      value: row.value,
      topLabel: row.value.toLocaleString('en-US'),
    }))
  }, [data])

  const visitYAxis = useMemo(() => {
    return computeCountAxis(visitChartData.map((row) => row.value))
  }, [visitChartData])

  const atRiskSummaryMetrics = useMemo((): AtRiskSummaryMetric[] => {
    if (!data) {
      return [
        {
          id: 'high-risk',
          label: 'High risk',
          value: loading ? '…' : '—',
          labelTone: 'danger',
        },
        { id: 'medium-risk', label: 'Medium risk', value: loading ? '…' : '—' },
        { id: 'low-risk', label: 'Low risk', value: loading ? '…' : '—' },
        {
          id: 'revenue-at-risk',
          label: 'Revenue at risk',
          value: loading ? '…' : '—',
        },
      ]
    }
    const { summary } = data.atRiskPatients
    return [
      {
        id: 'high-risk',
        label: 'High risk',
        value: formatKpiCount(summary.high),
        labelTone: 'danger',
      },
      {
        id: 'medium-risk',
        label: 'Medium risk',
        value: formatKpiCount(summary.medium),
      },
      {
        id: 'low-risk',
        label: 'Low risk',
        value: formatKpiCount(summary.low),
      },
      {
        id: 'revenue-at-risk',
        label: 'Revenue at risk',
        value: formatDollarK(summary.revenueAtRisk),
      },
    ]
  }, [data, loading])

  const atRiskAgeBuckets = useMemo((): AtRiskAgeBucket[] => {
    if (!data) {
      return AT_RISK_AGE_BUCKET_META.map((meta) => ({
        ...meta,
        percentage: 0,
      }))
    }
    const byLabel = new Map(
      data.atRiskByAge.buckets.map((b) => [b.label, b.percentage]),
    )
    return AT_RISK_AGE_BUCKET_META.map((meta) => ({
      ...meta,
      percentage: byLabel.get(meta.label) ?? 0,
    }))
  }, [data])

  const riskPatients = useMemo((): RiskPatientRow[] => {
    if (!data) return []
    return data.atRiskPatients.patients.map((row) => ({
      id: row.patientId,
      patientName: row.patientName,
      lastVisitDaysAgo: row.lastVisitDaysAgo,
      unscheduledTreatment: formatCurrency(row.unscheduledTreatment),
      noShowCount: row.noShowCount,
      outstandingBalance: formatCurrency(row.outstandingBalance),
      riskScore: row.riskScore,
      riskLevel: row.riskLevel,
    }))
  }, [data])

  const atRiskFilteredRows = useMemo((): RiskPatientRow[] => {
    switch (atRiskLevelFilter) {
      case 'all':
        return riskPatients
      case 'high':
        return riskPatients.filter((r) => r.riskLevel === 'High')
      case 'medium':
        return riskPatients.filter((r) => r.riskLevel === 'Medium')
      case 'low':
        return riskPatients.filter((r) => r.riskLevel === 'Low')
      case 'growth':
        return riskPatients.filter((r) => r.riskLevel === 'Growth')
      default:
        return riskPatients
    }
  }, [atRiskLevelFilter, riskPatients])

  const atRiskLevelFilterCell = useMemo(
    () => (
      <div role="group" aria-label="Filter by risk level">
        <ReportViewTabs
          options={AT_RISK_LEVEL_FILTER_TABS}
          value={atRiskLevelFilter}
          onChange={(value) => setAtRiskLevelFilter(value as AtRiskLevelFilter)}
          className="reports-patient-base-health__view-group"
        />
      </div>
    ),
    [atRiskLevelFilter],
  )

  const riskPatientNameColumn: ReportDataTableColumn<RiskPatientRow> = {
    key: 'patientName',
    label: 'Patient name',
    headerClassName: 'reports-patient-base-health__risk-table-name-col',
    cellClassName: 'reports-patient-base-health__risk-table-name-col',
  }

  const riskLastVisitColumn: ReportDataTableColumn<RiskPatientRow> = {
    key: 'lastVisitDaysAgo',
    label: 'Last visit (days ago)',
    headerClassName: 'reports-patient-base-health__risk-table-last-visit-col',
    cellClassName: 'reports-patient-base-health__risk-table-last-visit-col',
  }

  const riskLevelColumn: ReportDataTableColumn<RiskPatientRow> = {
    key: 'riskLevel',
    label: 'Risk level',
    headerClassName: 'reports-patient-base-health__risk-table-level-col',
    cellClassName: 'reports-patient-base-health__risk-table-level-col',
    render: (row) => (
      <span
        className={`reports-patient-base-health__risk-level reports-patient-base-health__risk-level--${row.riskLevel.toLowerCase()}`}
      >
        {row.riskLevel}
      </span>
    ),
  }

  const riskTableColumns: ReportDataTableColumn<RiskPatientRow>[] = [
    riskPatientNameColumn,
    riskLastVisitColumn,
    {
      key: 'noShowCount',
      label: 'No-show count',
      headerClassName: 'reports-patient-base-health__risk-table-noshow-col',
      cellClassName: 'reports-patient-base-health__risk-table-noshow-col',
    },
    riskLevelColumn,
  ]

  const atRiskTableRows = useMemo((): RiskPatientRow[] => {
    const rows = atRiskFullViewTableOnly ? riskPatients : atRiskFilteredRows
    return atRiskFullViewTableOnly
      ? rows
      : rows.slice(0, AT_RISK_CARD_ROW_LIMIT)
  }, [atRiskFilteredRows, atRiskFullViewTableOnly, riskPatients])

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.totalActivePatients.value,
      data?.netActiveGrowth.value,
      data?.retentionRate.value,
      data?.reactivationRate.value,
      data?.atRiskPatientCount.value,
    ),
  )
  const trendEmpty = shouldShowDashboardSectionEmpty(
    loading,
    trendRows.some((row) =>
      hasNonZeroDashboardValues(row.Active, row.Dormant, row.Lost),
    ),
  )
  const visitEmpty = shouldShowDashboardSectionEmpty(
    loading,
    visitChartData.some((row) => hasNonZeroDashboardValues(row.value)),
  )
  const atRiskAgeEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.atRiskPatients.summary.high,
      data?.atRiskPatients.summary.medium,
      data?.atRiskPatients.summary.low,
      data?.atRiskPatients.summary.revenueAtRisk,
      ...(data?.atRiskByAge.buckets.flatMap((bucket) => [
        bucket.value,
        bucket.percentage,
      ]) ?? []),
    ),
  )
  const atRiskTableEmpty = shouldShowDashboardSectionEmpty(
    loading,
    riskPatients.length > 0,
  )

  const handleAtRiskPatientsFullView = (): void => {
    expandCtx?.expand('pbh-at-risk')
  }

  const handleAtRiskPatientsExitFullView = (): void => {
    expandCtx?.exit()
  }

  return (
    <div className="reports-patient-base-health">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Patient Base Health">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={PATIENT_BASE_HEALTH_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <ReportExpandableSection sectionId="pbh-kpi">
        {kpiEmpty ? (
          <DashboardSectionEmptyState />
        ) : (
          <ReportKpiGrid items={kpiItems} />
        )}
      </ReportExpandableSection>

      <ReportExpandableSection sectionId="pbh-charts">
        <div className="reports-patient-base-health__charts-grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-base-health__chart-head">
              <ReportSectionHeading>
                Patient Population Trend
              </ReportSectionHeading>
            </div>
            {trendEmpty ? (
              <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
            ) : (
              <div className="reports-patient-base-health__chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendRows} margin={REPORT_LINE_CHART_MARGIN}>
                    <CartesianGrid
                      vertical={false}
                      strokeDasharray="0"
                      stroke={REPORT_CHART_GRID_STROKE}
                    />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={REPORT_CHART_AXIS_TICK}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={REPORT_CHART_AXIS_TICK}
                      width={REPORT_YAXIS_WIDTH}
                    />
                    <Tooltip contentStyle={REPORT_CHART_TOOLTIP_STYLE} />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 14, paddingTop: 0 }}
                    />
                    <Line
                      type="linear"
                      dataKey="Active"
                      stroke="var(--vivid-green-400)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                    />
                    <Line
                      type="linear"
                      dataKey="Dormant"
                      stroke="var(--orange-400)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                    />
                    <Line
                      type="linear"
                      dataKey="Lost"
                      stroke="var(--red-500)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-base-health__chart-head">
              <ReportSectionHeading>
                Last Visit Distribution
              </ReportSectionHeading>
            </div>
            {visitEmpty ? (
              <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
            ) : (
              <div className="reports-patient-base-health__chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={visitChartData}
                    margin={REPORT_BAR_CHART_MARGIN}
                    barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                  >
                    <CartesianGrid
                      vertical={false}
                      strokeDasharray="0"
                      stroke={REPORT_CHART_GRID_STROKE}
                    />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      tick={(props) => <VisitRangeTick {...props} />}
                      height={44}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      ticks={visitYAxis.ticks}
                      tickFormatter={visitYAxis.formatter}
                      tick={REPORT_CHART_AXIS_TICK}
                      width={visitYAxis.width}
                      allowDecimals={false}
                    />
                    <Tooltip
                      formatter={(value: number) =>
                        value.toLocaleString('en-US')
                      }
                      contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                    />
                    <Bar
                      dataKey="value"
                      radius={[8, 8, 8, 8]}
                      fill="var(--blue-400)"
                      barSize={100}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ReportSectionCard>
        </div>
      </ReportExpandableSection>

      <ReportExpandableSection sectionId="pbh-at-risk">
        <div
          className={[
            'reports-patient-base-health__risk-row',
            atRiskFullViewTableOnly
              ? 'reports-patient-base-health__risk-row--table-full-view'
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {!atRiskFullViewTableOnly ? (
            <ReportSectionCard className="reports-patient-base-health__chart-card reports-patient-base-health__risk-age-card">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  At-Risk patients by age
                </ReportSectionHeading>
              </div>
              {atRiskAgeEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <>
                  <AtRiskSummaryStrip metrics={atRiskSummaryMetrics} />
                  <div className="reports-patient-base-health__risk-content">
                    <div className="reports-patient-base-health__risk-pie">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={atRiskAgeBuckets}
                            dataKey="percentage"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            outerRadius={84}
                            paddingAngle={2}
                          >
                            {atRiskAgeBuckets.map((bucket) => (
                              <Cell key={bucket.id} fill={bucket.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number) => `${value}%`}
                            contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="reports-patient-base-health__risk-age-list">
                      {atRiskAgeBuckets.map((bucket) => (
                        <li
                          key={bucket.id}
                          className="reports-patient-base-health__risk-age-row"
                        >
                          <span className="reports-patient-base-health__risk-age-label-group">
                            <span
                              className="reports-patient-base-health__risk-age-swatch"
                              style={{ backgroundColor: bucket.color }}
                            />
                            <span className="reports-patient-base-health__risk-age-label">
                              {bucket.label}
                            </span>
                          </span>
                          <span className="reports-patient-base-health__risk-age-value">
                            {bucket.percentage}%
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </ReportSectionCard>
          ) : null}

          <ReportSectionCard className="reports-patient-base-health__chart-card reports-patient-base-health__risk-patients-card">
            <div className="reports-patient-base-health__chart-head">
              {atRiskFullViewTableOnly ? (
                <LinkButton
                  type="button"
                  underline={false}
                  leftIcon={<ArrowLeftIcon aria-hidden />}
                  className="reports-patient-base-health__risk-patients-title reports-patient-base-health__risk-patients-title--back"
                  onClick={handleAtRiskPatientsExitFullView}
                  aria-label="Exit full view of last visit distribution"
                >
                  <ReportSectionHeading>
                    Last visit distribution
                  </ReportSectionHeading>
                </LinkButton>
              ) : atRiskTableEmpty ? (
                <ReportSectionHeading>
                  Last visit distribution
                </ReportSectionHeading>
              ) : (
                <>
                  <LinkButton
                    type="button"
                    underline={false}
                    rightIcon={<ArrowRightIcon aria-hidden />}
                    className="reports-patient-base-health__risk-patients-title"
                    onClick={handleAtRiskPatientsFullView}
                    disabled={expandCtx == null}
                    aria-label="Open full view of last visit distribution"
                  >
                    <ReportSectionHeading>
                      Last visit distribution
                    </ReportSectionHeading>
                  </LinkButton>
                  <div className="reports-patient-base-health__chart-head-controls">
                    {atRiskLevelFilterCell}
                  </div>
                </>
              )}
            </div>
            {atRiskTableEmpty ? (
              <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
            ) : (
              <ReportDataTable
                key={
                  atRiskFullViewTableOnly
                    ? 'pbh-at-risk-fv'
                    : `pbh-at-risk-${atRiskLevelFilter}`
                }
                columns={riskTableColumns}
                rows={atRiskTableRows}
                rowKey={(row) => row.id}
                className={[
                  'reports-patient-base-health__risk-table',
                  atRiskFullViewTableOnly
                    ? 'reports-patient-base-health__risk-table--full-view'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                showFullViewButton={false}
                showExitFullViewButton={false}
                showPagination={atRiskFullViewTableOnly}
                previewRowLimit={
                  atRiskFullViewTableOnly ? undefined : AT_RISK_CARD_ROW_LIMIT
                }
                fullViewSectionId="pbh-at-risk"
                fullViewSearchWithFilter={PBH_AT_RISK_FULL_VIEW_SEARCH}
              />
            )}
          </ReportSectionCard>
        </div>
      </ReportExpandableSection>
    </div>
  )
}

export default PatientBaseHealthReport
